import React, { useEffect, useState, useCallback } from 'react';
import { money } from '../utils/format';
import axios from 'axios';
import {
  FaFileInvoiceDollar,
  FaFilePdf,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaSyncAlt,
  FaUsers,
} from 'react-icons/fa';
import { toastSuccess, toastError } from '../utils/toast';
import Spinner from './ui/Spinner';
import ConfirmDialog from './ui/ConfirmDialog';

// Formato moneda UYU
// El formateo de plata vive en utils/format.js (mismo criterio en toda la app).
const fmt = money;

const fmtFecha = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString('es-UY', {
      timeZone: 'America/Montevideo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(d);
  }
};

const ESTADO_BADGE = {
  pendiente_pago: { label: 'Pendiente', cls: 'bg-amber/15 text-amber border border-amber/30' },
  pagado:         { label: 'Pagado',            cls: 'bg-lime/15 text-lime border border-lime/30' },
  cancelado:      { label: 'Cancelado',         cls: 'bg-red/15 text-red border border-red/30' },
};

export default function GenerarCierrePage() {
  const apiUrl = process.env.REACT_APP_API_URL;

  const [resumen, setResumen] = useState({ empleadosConPropina: 0, totalPendiente: 0, ultimoCierre: null });
  const [lotes, setLotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [markingId, setMarkingId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);

  // Detalle expandible por lote (lista de empleados + checkbox de pago individual)
  const [expandedLote, setExpandedLote] = useState(null);
  const [loteDetail, setLoteDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [markingRetiroId, setMarkingRetiroId] = useState(null);

  // Dialogos de confirmación
  const [confirmGenerar, setConfirmGenerar] = useState(false);
  const [confirmMarcar, setConfirmMarcar] = useState({ open: false, loteId: null });
  const [confirmCancelar, setConfirmCancelar] = useState({ open: false, loteId: null });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [resumenRes, lotesRes] = await Promise.all([
        axios.get(`${apiUrl}/restaurante/cierres/resumen`, { headers }),
        axios.get(`${apiUrl}/restaurante/cierres`, { headers }),
      ]);
      setResumen(resumenRes.data || {});
      setLotes(lotesRes.data?.lotes || []);
    } catch (err) {
      console.error('Error al cargar cierres:', err);
      toastError('No pudimos cargar los cierres.');
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleGenerar = async () => {
    if (generating) return;
    setGenerating(true);
    setConfirmGenerar(false);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${apiUrl}/restaurante/cerrar-periodo`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { empleadosIncluidos, total, faltanDatosBancarios } = res.data || {};
      toastSuccess(`Cierre generado: ${empleadosIncluidos} empleados, ${fmt(total)} a pagar.`);
      if (faltanDatosBancarios?.length) {
        toastError(
          `Atención: ${faltanDatosBancarios.length} empleado(s) sin datos bancarios completos. ` +
          `Completalos antes de pagar.`
        );
      }
      fetchAll();
    } catch (err) {
      console.error('Error al generar cierre:', err);
      const msg = err.response?.data?.error || 'No pudimos generar el cierre.';
      toastError(msg);
    } finally {
      setGenerating(false);
    }
  };

  const fetchLoteDetail = async (loteId) => {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${apiUrl}/restaurante/cierre/${loteId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data?.lote || null;
  };

  const toggleLote = async (loteId) => {
    if (expandedLote === loteId) {
      setExpandedLote(null);
      setLoteDetail(null);
      return;
    }
    setExpandedLote(loteId);
    setLoteDetail(null);
    setLoadingDetail(true);
    try {
      setLoteDetail(await fetchLoteDetail(loteId));
    } catch (err) {
      console.error('Error al cargar detalle del cierre:', err);
      toastError('No pudimos cargar el detalle del cierre.');
      setExpandedLote(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  const marcarRetiro = async (loteId, retiroId) => {
    if (markingRetiroId) return;
    setMarkingRetiroId(retiroId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${apiUrl}/restaurante/cierre/${loteId}/retiro/${retiroId}/marcar-pagado`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toastSuccess('Empleado marcado como pagado.');
      // Refrescamos el detalle abierto y la lista de lotes (el estado del lote
      // pasa a 'pagado' cuando todos sus empleados están completados).
      setLoteDetail(await fetchLoteDetail(loteId));
      fetchAll();
    } catch (err) {
      console.error('Error al marcar empleado como pagado:', err);
      toastError(err.response?.data?.error || 'No pudimos marcar el pago.');
    } finally {
      setMarkingRetiroId(null);
    }
  };

  const handleDescargarPdf = async (loteId) => {
    if (downloadingId) return;
    setDownloadingId(loteId);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${apiUrl}/restaurante/cierre/${loteId}/documento.pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );
      // Descargar el PDF como archivo
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `documento_pagos_${loteId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error al descargar PDF:', err);
      toastError('No pudimos descargar el documento.');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleMarcarPagado = async () => {
    const { loteId } = confirmMarcar;
    if (!loteId || markingId) return;
    setMarkingId(loteId);
    setConfirmMarcar({ open: false, loteId: null });
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${apiUrl}/restaurante/cierre/${loteId}/marcar-pagado`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toastSuccess('Cierre marcado como pagado. Saldos actualizados.');
      fetchAll();
    } catch (err) {
      console.error('Error al marcar como pagado:', err);
      toastError(err.response?.data?.error || 'No pudimos actualizar el estado.');
    } finally {
      setMarkingId(null);
    }
  };

  const handleCancelar = async () => {
    const { loteId } = confirmCancelar;
    if (!loteId || cancellingId) return;
    setCancellingId(loteId);
    setConfirmCancelar({ open: false, loteId: null });
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${apiUrl}/restaurante/cierre/${loteId}/cancelar`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toastSuccess('Cierre cancelado.');
      fetchAll();
    } catch (err) {
      console.error('Error al cancelar cierre:', err);
      toastError(err.response?.data?.error || 'No pudimos cancelar el cierre.');
    } finally {
      setCancellingId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-ink flex items-center gap-3">
            <FaFileInvoiceDollar className="text-purple" />
            Generar pagos
          </h1>
          <p className="si-body mt-1">
            Cerrá el período cuando quieras y descargá el documento para llevar al banco.
          </p>
        </div>
        <button
          onClick={fetchAll}
          className="si-btn-ghost text-sm flex items-center gap-2"
          disabled={loading}
          title="Refrescar"
        >
          <FaSyncAlt className={loading ? 'animate-spin' : ''} />
          Refrescar
        </button>
      </div>

      {/* Cards de resumen */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-surface border border-line rounded-2xl p-5">
          <div className="text-xs uppercase tracking-wider text-ink-3">Mozos con pedido</div>
          <div className="text-3xl font-semibold text-ink mt-2 tabular-nums">
            {resumen.empleadosConPropina}
          </div>
          <div className="text-xs text-ink-3 mt-1">
            pidieron retirar y esperan el cierre
          </div>
        </div>

        <div className="bg-surface border border-line rounded-2xl p-5">
          <div className="text-xs uppercase tracking-wider text-ink-3">Total pedido</div>
          <div className="text-3xl font-semibold text-lime mt-2 tabular-nums">
            {fmt(resumen.totalPendiente)}
          </div>
          <div className="text-xs text-ink-3 mt-1">
            lo que pidieron los mozos
          </div>
        </div>

        <div className="bg-surface border border-line rounded-2xl p-5">
          <div className="text-xs uppercase tracking-wider text-ink-3">Último cierre</div>
          <div className="si-h3 mt-2">
            {resumen.ultimoCierre ? fmtFecha(resumen.ultimoCierre.fecha) : '—'}
          </div>
          <div className="text-xs text-ink-3 mt-1">
            {resumen.ultimoCierre ? 'fecha de generación' : 'no hay cierres aún'}
          </div>
        </div>
      </div>

      {/* CTA grande para generar cierre */}
      <div className="bg-gradient-to-br from-purple/10 to-purple/5 border border-purple/30 rounded-2xl p-6 flex items-center justify-between">
        <div>
          <h2 className="si-h2">¿Listo para pagar a tu equipo?</h2>
          <p className="text-sm text-ink-2 mt-1">
            Generá un cierre con los {resumen.empleadosConPropina} pedidos de retiro pendientes,
            descargá el PDF y llevalo al banco. Después podés ir marcando a cada empleado a medida que le pagás.
          </p>
        </div>
        <button
          onClick={() => setConfirmGenerar(true)}
          disabled={generating || resumen.empleadosConPropina === 0}
          className="si-btn-primary text-sm whitespace-nowrap"
          title={resumen.empleadosConPropina === 0 ? 'No hay propinas pendientes' : ''}
        >
          {generating && <Spinner size={14} className="mr-2" />}
          {generating ? 'Generando...' : 'Cerrar período'}
        </button>
      </div>

      {/* Lista de cierres anteriores */}
      <div className="bg-surface border border-line rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-line">
          <h2 className="si-h3">Cierres anteriores</h2>
        </div>

        {loading && lotes.length === 0 ? (
          <div className="p-10 text-center text-ink-3">
            <Spinner size={20} className="mx-auto mb-3" />
            Cargando cierres...
          </div>
        ) : lotes.length === 0 ? (
          <div className="p-10 text-center text-ink-3">
            Todavía no generaste ningún cierre.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-2 text-ink-3 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-5 py-3 text-left">Fecha</th>
                  <th className="px-5 py-3 text-left">Empleados</th>
                  <th className="px-5 py-3 text-right">Total</th>
                  <th className="px-5 py-3 text-center">Estado</th>
                  <th className="px-5 py-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {lotes.map((lote) => {
                  const badge = ESTADO_BADGE[lote.estado] || ESTADO_BADGE.pendiente_pago;
                  const isDownloading = downloadingId === lote.loteId;
                  const isMarking = markingId === lote.loteId;
                  const isCancelling = cancellingId === lote.loteId;
                  const isExpanded = expandedLote === lote.loteId;

                  return (
                    <React.Fragment key={lote.loteId}>
                    <tr className="border-t border-line hover:bg-surface-2/30">
                      <td className="px-5 py-3 text-ink-2">{fmtFecha(lote.fechaGenerado)}</td>
                      <td className="px-5 py-3 text-ink-2 tabular-nums">{lote.cantidadEmpleados}</td>
                      <td className="px-5 py-3 text-ink font-medium text-right tabular-nums">{fmt(lote.total)}</td>
                      <td className="px-5 py-3 text-center">
                        <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${badge.cls}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => toggleLote(lote.loteId)}
                            className="si-btn-ghost text-xs flex items-center gap-1.5 whitespace-nowrap shrink-0"
                            title="Ver empleados y marcar pagos uno por uno"
                          >
                            <FaUsers className="shrink-0" />
                            {isExpanded ? 'Ocultar' : 'Ver empleados'}
                          </button>

                          <button
                            onClick={() => handleDescargarPdf(lote.loteId)}
                            disabled={isDownloading}
                            className="si-btn-ghost text-xs flex items-center gap-1.5 whitespace-nowrap shrink-0"
                            title="Descargar PDF para el banco"
                          >
                            {isDownloading ? <Spinner size={12} /> : <FaFilePdf />}
                            PDF
                          </button>

                          {lote.estado === 'pendiente_pago' && (
                            <>
                              <button
                                onClick={() => setConfirmMarcar({ open: true, loteId: lote.loteId })}
                                disabled={isMarking}
                                className="si-btn-success text-xs flex items-center gap-1.5 whitespace-nowrap shrink-0"
                                title="Marcar como pagados a todos los empleados que falten"
                              >
                                {isMarking ? <Spinner size={12} /> : <FaCheckCircle />}
                                Marcar todo pagado
                              </button>
                              <button
                                onClick={() => setConfirmCancelar({ open: true, loteId: lote.loteId })}
                                disabled={isCancelling}
                                className="si-btn-danger text-xs flex items-center gap-1.5 whitespace-nowrap shrink-0"
                                title="Cancelar este cierre"
                              >
                                {isCancelling ? <Spinner size={12} /> : <FaTimesCircle />}
                                Cancelar
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr className="border-t border-line bg-surface-2/20">
                        <td colSpan={5} className="px-5 py-4">
                          {loadingDetail ? (
                            <div className="text-center text-ink-3 py-4">
                              <Spinner size={16} className="mx-auto mb-2" />
                              Cargando empleados...
                            </div>
                          ) : loteDetail && loteDetail.retiros ? (
                            <div className="space-y-2">
                              <div className="text-xs uppercase tracking-wider text-ink-3 mb-2">
                                Tildá a cada empleado cuando le hayas girado el pago
                              </div>
                              {loteDetail.retiros.map((r) => {
                                const pagado = r.estado === 'completado';
                                const cancelado = r.estado === 'cancelado';
                                return (
                                  <div
                                    key={r.retiroId}
                                    className="flex items-center justify-between gap-3 py-2 px-3 rounded-lg bg-surface border border-line"
                                  >
                                    <label className="flex items-center gap-3 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={pagado}
                                        disabled={pagado || cancelado || markingRetiroId === r.retiroId}
                                        onChange={() => marcarRetiro(lote.loteId, r.retiroId)}
                                        style={{ accentColor: '#22c55e', width: 16, height: 16 }}
                                      />
                                      <span className={`text-sm ${pagado ? 'text-ink-3 line-through' : 'text-ink'}`}>
                                        {r.Nombre} {r.Apellido}
                                      </span>
                                    </label>
                                    <div className="flex items-center gap-3">
                                      <span className="text-sm font-medium tabular-nums text-ink-2">{fmt(r.propina)}</span>
                                      <span
                                        className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                                          pagado
                                            ? 'bg-lime/15 text-lime'
                                            : cancelado
                                            ? 'bg-red/15 text-red'
                                            : 'bg-amber/15 text-amber'
                                        }`}
                                      >
                                        {pagado ? 'Pagado' : cancelado ? 'Cancelado' : 'Pendiente'}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-center text-ink-3 py-4">No se pudo cargar el detalle.</div>
                          )}
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Aviso de empleados sin datos bancarios */}
      {resumen.empleadosConPropina > 0 && (
        <div className="bg-amber/5 border border-amber/30 rounded-xl p-4 flex items-start gap-3">
          <FaExclamationTriangle className="text-amber mt-0.5 flex-shrink-0" />
          <div className="text-sm text-ink-2">
            <strong className="text-ink">Antes de generar el cierre:</strong> verificá que todos los empleados
            tengan datos bancarios completos (banco, tipo de cuenta y número). Los que falten aparecerán
            marcados en el PDF con ⚠ — vas a tener que completarlos manualmente con el contador o pagarles por otro medio.
          </div>
        </div>
      )}

      {/* Confirmaciones */}
      <ConfirmDialog
        isOpen={confirmGenerar}
        onClose={() => setConfirmGenerar(false)}
        onConfirm={handleGenerar}
        title="Generar cierre de pagos"
        message={
          `Vas a agrupar los pedidos de retiro de ${resumen.empleadosConPropina} empleado(s), ` +
          `por un total de ${fmt(resumen.totalPendiente)}, en un cierre. ` +
          `Vas a poder descargar el PDF para el banco y marcar a cada empleado a medida que le pagás.`
        }
        confirmLabel="Sí, generar cierre"
        cancelLabel="Cancelar"
        variant="primary"
        loading={generating}
      />

      <ConfirmDialog
        isOpen={confirmMarcar.open}
        onClose={() => setConfirmMarcar({ open: false, loteId: null })}
        onConfirm={handleMarcarPagado}
        title="Confirmar pago realizado"
        message={
          `¿Confirmás que ya transferiste el dinero a todos los empleados que falten de este cierre? ` +
          `Quedan marcados como pagados y no se puede revertir.`
        }
        confirmLabel="Sí, ya pagué"
        cancelLabel="Cancelar"
        variant="primary"
        loading={!!markingId}
      />

      <ConfirmDialog
        isOpen={confirmCancelar.open}
        onClose={() => setConfirmCancelar({ open: false, loteId: null })}
        onConfirm={handleCancelar}
        title="Cancelar cierre"
        message={
          `Vas a cancelar este cierre. A cada empleado se le devuelve el saldo (vuelve a estar disponible para que lo pida de nuevo). ` +
          `Solo se puede cancelar si todavía no le pagaste a nadie. Esta acción no se puede deshacer.`
        }
        confirmLabel="Sí, cancelar cierre"
        cancelLabel="No, mantener"
        variant="danger"
        loading={!!cancellingId}
      />
    </div>
  );
}
